/**
 * useSchoolApi – hooks for school search, enrollment requests, school
 * management, and join code validation.
 *
 * IDENTITY CONTRACT (R38):
 *  - The school portal is its OWN entity layer, NOT a variant of the shop
 *    system. School memberships live in `school_memberships(user_id, school_id, role)`
 *    with role enum `('school_admin' | 'instructor')`. Students live in
 *    `student_enrollments(user_id, school_id, cohort_id, status)` — they
 *    are a STATUS, not a school-membership role (PROD-A in R38 backlog).
 *  - These hooks MUST NOT take `activeShop` / `shopId` / `team_id` /
 *    `org_id` as input or read them from `useAuth()`. School flows are
 *    identity-scoped (auth user) and school-scoped (`school_id`); the
 *    Floo Network shop context is a separate concern.
 *  - Writes always anchor on `user_id: auth.uid()` for personal data and
 *    `school_id: <known-school>` for school-scoped data.
 *  - One-way coupling rule: school code MAY READ Passport data (talent
 *    matching, profile display) but MUST NOT WRITE to passport_* tables.
 *    Passport stays user-owned and portable across schools and shops.
 *
 * SCALING NOTE: `useMySchoolMembership` currently uses `.maybeSingle()`
 * which assumes one school per user. When multi-school instructors become
 * a product requirement, switch to a list-returning hook.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';

// ---------- Types ----------
export interface SchoolSearchResult {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
}

export interface EnrollmentRequest {
  id: string;
  user_id: string;
  school_id: string;
  cohort_id: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export interface StudentEnrollment {
  id: string;
  user_id: string;
  school_id: string;
  cohort_id: string | null;
  status: 'enrolled' | 'completed' | 'withdrawn';
  expected_grad_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface JoinCodeValidation {
  cohort_id: string;
  cohort_name: string;
  program_name: string;
  school_id: string;
  school_name: string;
}

// ---------- Search Schools (RPC) ----------
export function useSearchSchools(query: string) {
  return useQuery({
    queryKey: ['search-schools', query],
    queryFn: async () => {
      if (!query || query.length < 2) return [];
      const { data, error } = await supabase.rpc('search_schools', { p_query: query });
      if (error) throw error;
      return (data ?? []) as SchoolSearchResult[];
    },
    enabled: query.length >= 2,
  });
}

// ---------- Validate Join Code (RPC) ----------
// SECURITY NOTE (R39): `validate_cohort_join_code` is callable by any
// authenticated user and returns school + program + cohort identifiers
// for active codes. The keyspace (8 alphanumeric chars ≈ 32^8 ≈ 1
// trillion) makes guessing infeasible without rate-limiting, but a
// concerted enumeration attack is a theoretical concern.
//   Mitigation today:
//     - Codes are cryptographically random.
//     - The RPC requires `authenticated` role (no anonymous access).
//     - The submit-request edge function re-validates server-side.
//   Backlog: add per-user rate-limit on the RPC if usage telemetry
//   surfaces enumeration patterns.
//
// `retry: false` prevents accidentally amplifying invalid attempts; we
// only retry intentionally on user retype.
export function useValidateJoinCode(code: string) {
  const normalized = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const isComplete = normalized.length === 8;

  return useQuery({
    queryKey: ['validate-join-code', normalized],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('validate_cohort_join_code', { p_join_code: code });
      if (error) throw error;
      if (!data || data.length === 0) return null;
      return data[0] as JoinCodeValidation;
    },
    enabled: isComplete,
    retry: false,
  });
}

// ---------- My Enrollment Requests ----------
export function useMyEnrollmentRequests() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-enrollment-requests', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_enrollment_requests')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as EnrollmentRequest[];
    },
    enabled: !!user,
  });
}

// ---------- My Enrollments ----------
export function useMyEnrollments() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-enrollments', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_enrollments')
        .select('*')
        .eq('user_id', user!.id);
      if (error) throw error;
      return data as StudentEnrollment[];
    },
    enabled: !!user,
  });
}

// ---------- Submit Enrollment Request (via edge function) ----------
export function useSubmitEnrollmentRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ joinCode }: { joinCode: string }) => {
      const { data, error } = await supabase.functions.invoke('manage-student-enrollment', {
        body: { action: 'submit_request', joinCode },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-enrollment-requests'] });
      toast({ title: 'Request sent', description: 'Your enrollment request has been submitted — awaiting instructor approval.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message || 'Failed to submit request', variant: 'destructive' });
    },
  });
}

// ---------- School Staff: Pending Requests ----------
export function useSchoolEnrollmentRequests(schoolId: string | undefined) {
  return useQuery({
    queryKey: ['school-enrollment-requests', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_enrollment_requests')
        .select('*')
        .eq('school_id', schoolId!)
        .eq('status', 'pending')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as EnrollmentRequest[];
    },
    enabled: !!schoolId,
  });
}

// ---------- Approve / Reject Request ----------
export function useManageEnrollmentRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { action: 'approve_request' | 'reject_request'; requestId: string; cohort_id?: string; expected_grad_date?: string }) => {
      const { data, error } = await supabase.functions.invoke('manage-student-enrollment', {
        body: params,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['school-enrollment-requests'] });
      qc.invalidateQueries({ queryKey: ['my-enrollment-requests'] });
      qc.invalidateQueries({ queryKey: ['my-enrollments'] });
      toast({ title: vars.action === 'approve_request' ? 'Approved' : 'Rejected' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });
}

// ---------- Manage School (create/update school, programs, cohorts) ----------
export function useManageSchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke('manage-school', { body: params });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-enrollments'] });
      qc.invalidateQueries({ queryKey: ['school-cohorts'] });
      qc.invalidateQueries({ queryKey: ['school-programs'] });
      toast({ title: 'Saved' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });
}

// ---------- Rotate Join Code ----------
export function useRotateJoinCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cohortId: string) => {
      const { data, error } = await supabase.functions.invoke('manage-school', {
        body: { action: 'rotate_join_code', cohortId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school-cohorts'] });
      toast({ title: 'Join code rotated', description: 'Previous code is now invalid.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });
}

// ---------- Manage School Membership ----------
export function useManageSchoolMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke('manage-school-membership', { body: params });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school-staff'] });
      toast({ title: 'Updated' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });
}

// ---------- My School Membership ----------
// FIX-R38-HIGH-1: SCALING WARNING.
// `.maybeSingle()` assumes ONE school membership per user. This is true
// today (R38 product state) but the schema does not enforce it — a user
// COULD have school_memberships rows at multiple schools (e.g., an
// instructor moonlighting at two trade schools). When that becomes a
// product requirement, replace this with a list-returning hook
// (`useMySchoolMemberships`) and an "active school" selector parallel to
// `setActiveShop`. Until then, `.maybeSingle()` will throw if the schema
// drifts to multi-row, surfacing the regression loudly rather than
// silently picking the first row.
export function useMySchoolMembership() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-school-membership', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_memberships')
        .select('*, schools(id, name, city, state)')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

// ---------- School Programs ----------
export function useSchoolPrograms(schoolId: string | undefined) {
  return useQuery({
    queryKey: ['school-programs', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_programs')
        .select('*')
        .eq('school_id', schoolId!)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!schoolId,
  });
}

// ---------- School Cohorts ----------
export function useSchoolCohorts(schoolId: string | undefined) {
  return useQuery({
    queryKey: ['school-cohorts', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_cohorts')
        .select('*, school_programs!inner(school_id, name)')
        .eq('school_programs.school_id', schoolId!)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!schoolId,
  });
}

// ---------- School Staff ----------
export function useSchoolStaff(schoolId: string | undefined) {
  return useQuery({
    queryKey: ['school-staff', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_memberships')
        .select('*, profiles(id, full_name, email)')
        .eq('school_id', schoolId!)
        .order('role');
      if (error) throw error;
      return data;
    },
    enabled: !!schoolId,
  });
}
